---
title: "Terms of Service"
effective: "2026-07-23"
updated: "2026-07-30"
---

These Terms of Service ("**Terms**") are a binding agreement between you and
{publicEnv.siteLegalEntity} ("**Company**", "**we**", "**us**", or "**our**").
They govern your use of {publicEnv.siteTitle} at `cents.fyi`, including its
website, virtual family ledger, child views, exports, support, and billing
features (together, the "**Services**").

By creating or joining a family workspace, providing parental consent, adding a
payment method, purchasing a subscription, or otherwise using the Services, you
agree to these Terms and our [Privacy Policy](/privacy). If you act for a child
or another person, you represent that you have legal authority to do so. If you
do not agree, do not use the Services.

## 1 Eligibility, family authority, and accounts

### 1.1 Adult-created workspaces

Only an adult with legal capacity who is a parent or legal guardian authorized
to supervise the family may create a workspace. The adult is responsible for
reviewing the children's privacy notice and providing the required consent
before adding a child.

A person below the age of majority may use only a child account created and
authorized by that person's parent or legal guardian. A child may not create a
workspace, authorize a purchase, or represent that the child is an adult.

The family owner and each guardian represent that they have the authority
required to add the family members, emails, and ledger information they submit.
Do not add a person or disclose information about that person if you lack that
authority.

### 1.2 Family roles

The first authenticated person who creates the workspace is normally the
**family owner** and, by doing so, represents that they meet Section 1.1. The
owner can add members, transfer ownership, remove another guardian, and
permanently delete the workspace. Other **guardians** can manage the household,
child ledgers, billing, and exports but cannot remove a guardian or delete the
whole workspace.

A **child** can have a profile without an email. If a guardian enables a
separate child login, its ledger access is view-only and it can see only the
child's own ledger. The child can personalize that child's avatar colors and
optional mascot. Guardians are responsible for supervising child use and
deciding when separate access is appropriate.

The owner should transfer ownership before leaving the family. Ownership does
not establish or decide legal custody, parentage, property ownership, or
financial rights outside the Services.

### 1.3 Registration and security

You may sign in through a verified email link, one-time code, or approved
third-party identity provider. Sign-in and account creation use the same flow:
if the verified identity is not already associated with an account, continuing
creates one. You agree to provide accurate information and keep it current.

You are responsible for protecting email accounts, login links, codes, sessions,
provider credentials, devices, and downloaded exports. Promptly contact
{publicEnv.siteSupportEmail} if you suspect unauthorized access. We are not
responsible for losses caused by a failure to protect access credentials, except
where applicable law provides otherwise.

You may not sell, rent, or transfer an account. Family ownership may be
transferred only through the provided family control.

## 2 What the Services are

### 2.1 A virtual record, not a financial account

The Services let guardians maintain a private record of virtual credits, debits,
balances, descriptions, recurring deposits, and corrections for family education
and household coordination.

The Services are **not** a bank, credit union, payment service, money
transmitter, stored-value product, prepaid account, escrow service, investment
account, trust account, credit product, or financial adviser. We do not hold,
receive, safeguard, transfer, insure, or guarantee the money represented by a
ledger.

A displayed balance records only the family's own understanding. It does not
prove that money exists, belongs to a particular person, is held elsewhere, or
must be paid. Families are solely responsible for any real-world money,
property, tax, custody, or reimbursement arrangement.

### 2.2 Entries, schedules, and corrections

Guardians are responsible for the accuracy, fairness, legality, descriptions,
dates, and amounts they enter. Children cannot create or modify entries.

Recurring deposits create virtual entries according to the schedule and time
zone selected by a guardian. Device, network, provider, daylight-saving, or
software conditions can delay processing. Guardians must review scheduled
entries and correct errors.

Guardians can fix nonfinancial entry details while retaining edit history.
Changes to an amount, entry type, or effective date use a reversing entry and
replacement so the family can understand how a balance changed. The original,
reversal, and replacement can remain visible as audit history. Removing a
guardian does not erase the author label on prior entries or schedules.

### 2.3 Availability and changes

We work to keep the Services available, but maintenance, security incidents,
provider outages, legal requirements, or other events may interrupt access. We
may correct, update, add, remove, or discontinue a feature.

If discontinuation materially affects paid access, we will provide any notice or
remedy required by law and may provide an additional remedy at our discretion.
We do not promise a particular roadmap, uptime level, or perpetual operation.

## 3 Trials, subscriptions, and payment

The price, currency, billing interval, trial, renewal date, included features,
and other offer details shown on the pricing, billing, and payment-processor
pages at the time of authorization form part of these Terms. If an
offer-specific term conflicts with a general description here, the
offer-specific term controls for that transaction.

### 3.1 Free trial

When offered, a trial provides temporary access for the number of days displayed
before creation. No payment card is required to begin the current trial offer.

If you do not add a payment method, paid access stops when the trial and any
payment-recovery period end. If you add a payment method, the authorization
shown beside that control states the selected price, interval, and expected
first charge date. You must expressly accept it before leaving for the payment
processor.

We may change, limit, or withdraw a future trial offer. Unless required by law,
a person or family is not entitled to repeated trials.

### 3.2 Recurring authorization and automatic renewal

By adding a payment method and accepting the displayed recurring authorization,
the adult guardian authorizes us and our payment processor to charge the
selected price, applicable taxes, and disclosed fees to that payment method when
paid service begins and automatically at the selected monthly or annual interval
until canceled.

We preserve verification of the authorization as required by applicable law.

Your Billing page shows the current plan, subscription status, period dates,
next expected payment information when available, and cancellation controls. You
are responsible for keeping the payment method current.

If a charge fails, the payment processor may retry it according to the billing
settings and the Services may restrict access. You remain responsible for an
authorized amount due.

### 3.3 Plan changes

An authorized guardian can select an available monthly or annual plan in
Billing. Before a plan change takes effect, the Services display the plan and
price. The payment processor can apply prorations, credits, or a new billing
date as shown during the change.

### 3.4 Cancellation

An authorized guardian may cancel through **Billing → Cancel subscription**
without contacting support. Cancellation stops automatic renewal and normally
takes effect at the end of the current paid or trial period. The Billing page
shows the effective date. No further recurring charge is authorized after
cancellation takes effect unless a guardian later resubscribes.

Subscription cancellation and data deletion are separate. After a Cents FYI
subscription ends, the family workspace is retained for 90 days so the family
can resubscribe, export, or delete it. The workspace's family content and
sign-in identities are then scheduled for permanent deletion, while limited
billing and legal records may remain as described in the Privacy Policy. The
family owner may use **Delete family workspace** at any time to cancel live
billing and immediately close the workspace.

### 3.5 Refunds and mandatory rights

Except where required by law or expressly stated in an offer, charges are
non-refundable after paid access is provided, and cancellation does not create a
credit for unused time in a current period.

Contact {publicEnv.siteSupportEmail} if you believe a charge was unauthorized,
duplicated, incorrect, or failed to provide the access described. Nothing in
these Terms limits a mandatory cooling-off, withdrawal, conformity, refund,
cancellation, or other consumer right that applies where you live.

### 3.6 Price changes and taxes

We may change prices for future offers. We will provide the advance notice
required by law before changing an existing recurring charge. The notice will
state when the change takes effect so the guardian can cancel beforehand.

Prices are displayed in U.S. dollars unless stated otherwise. You are
responsible for taxes and charges disclosed before authorization. We may collect
and remit taxes where required.

### 3.7 Payment processor

Our payment processor handles payment methods and related billing details on
pages it controls. Your use of those pages is also subject to the processor's
terms and privacy policy. A pending, abandoned, reversed, disputed, or failed
payment may not provide or may later remove paid access.

## 4 Family content and license

### 4.1 Your family content

As between you and the Company, the family retains its rights in names, ledger
entries, descriptions, schedules, and other content a guardian submits
("**Family Content**").

You grant us a limited, worldwide, non-exclusive license to host, copy, process,
transmit, display, export, secure, and back up Family Content only as needed to
provide, protect, support, and legally operate the Services. This license ends
when the content is deleted, except for limited backup, legal, security, and
dispute copies described in the Privacy Policy.

You represent that you have the rights and family authority needed to submit
Family Content and grant this license. Do not place unlawful, infringing,
abusive, highly sensitive, or unnecessary personal information in a ledger
description.

### 4.2 Our Services

The Services, software, visual design, branding, documentation, and other
Company material are owned by the Company or its licensors and protected by
intellectual-property laws.

Subject to these Terms, we grant each authorized family member a limited,
revocable, non-exclusive, non-transferable license to use the Services for the
family's private, non-commercial household purposes.

### 4.3 Feedback

If you voluntarily send corrections, ideas, or feedback, you grant us a
worldwide, perpetual, irrevocable, royalty-free right to use it without
restriction or compensation. This does not transfer ownership of unrelated
Family Content, and personal information remains governed by the Privacy Policy.

## 5 Acceptable use

You agree not to:

1. Break a law or infringe another person's rights while using the Services.
2. Create a workspace while misrepresenting adult or guardian authority.
3. Add, monitor, or disclose information about a child without required parental
   or legal authority.
4. Use a child's optional login for an adult or let a child use guardian
   controls.
5. Share verification codes or intentionally provide unauthorized account
   access.
6. Treat a virtual balance as money held, insured, guaranteed, or transferred by
   us, or misrepresent it to another person.
7. Circumvent access controls, probe for vulnerabilities without written
   permission, or access another family's data.
8. Introduce malware or interfere with the security, integrity, availability, or
   normal operation of the Services.
9. Scrape, automate, or reverse engineer the Services in a way that burdens
   them, extracts private data, or evades a restriction, except where law
   expressly permits.
10. Use the Services to deceive, exploit, threaten, harass, or harm a child or
    another person.

We may investigate suspected misuse and take proportionate action, including
limiting requests, revoking a session, suspending access, preserving relevant
records, or contacting appropriate authorities.

## 6 Privacy and children's information

Our [Privacy Policy](/privacy) explains collection, family visibility,
providers, child-specific safeguards, parental consent, exports, deletion, and
retention.

Guardians must review the direct children's notice and provide any consent
required by law before adding a child. A guardian may disable a child login,
delete a child ledger, withdraw consent, or contact support. We may pause child
access and require a new guardian review when the notice materially changes or
when consent is missing.

## 7 Third-party services

The Services may interact with optional identity and payment providers. The
Privacy Policy identifies current providers and their roles. Provider-controlled
services are governed by their own terms and policies.

We do not control a third-party service and are not responsible for its
availability, security, accuracy, or conduct. Your dealings with a third party
are between you and that third party, subject to mandatory law.

## 8 Suspension, termination, and deletion

You may stop using the Services at any time. Guardians can revoke a child login;
the owner can remove another guardian or delete the workspace.

We may suspend or terminate access if a person materially or repeatedly violates
these Terms, lacks required family authority or consent, creates a security or
legal risk, fails to pay an amount due, or misuses the Services. When
reasonable, we will give notice and an opportunity to cure, but we may act
immediately to protect a child, family, the Company, or another person.

Upon termination, the license to use the Services ends. Content handling follows
the Privacy Policy and any mandatory law. Provisions that by their nature should
survive remain in effect, including payment obligations, intellectual property,
audit history, warranty disclaimers, liability limits, and dispute terms.

## 9 Disclaimers

**TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICES ARE PROVIDED “AS IS” AND
“AS AVAILABLE.” THE COMPANY AND ITS LICENSORS DISCLAIM ALL EXPRESS, IMPLIED, AND
STATUTORY WARRANTIES, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, AND UNINTERRUPTED OR
ERROR-FREE OPERATION.**

We do not guarantee a financial, educational, parenting, behavioral, tax,
custody, or other outcome. We do not guarantee that an entry, schedule,
correction, balance, export, notification, or provider response is complete,
timely, or error-free. Guardians must review the family record and maintain any
separate real-world financial records they need.

Some jurisdictions do not allow certain disclaimers, so some of the above may
not apply. You retain any warranty or consumer protection that cannot lawfully
be excluded.

## 10 Limitation of liability

**TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY AND ITS OWNERS,
AFFILIATES, EMPLOYEES, CONTRACTORS, LICENSORS, AND SERVICE PROVIDERS WILL NOT BE
LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL
DAMAGES, OR FOR LOST PROFITS, REVENUE, DATA, GOODWILL, FAMILY FUNDS, OR BUSINESS
INTERRUPTION, ARISING FROM OR RELATING TO THE SERVICES OR THESE TERMS.**

**TO THE MAXIMUM EXTENT PERMITTED BY LAW, THEIR TOTAL AGGREGATE LIABILITY FOR
ALL CLAIMS ARISING FROM OR RELATING TO THE SERVICES OR THESE TERMS WILL NOT
EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US FOR THE SERVICES DURING THE 12
MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM OR (B) US$100.**

These limits apply regardless of legal theory and even if a remedy fails of its
essential purpose. They do not limit liability that cannot lawfully be limited,
including liability for fraud, willful misconduct, or other matters excluded by
applicable law.

## 11 Indemnification

To the extent permitted by law, you agree to defend, indemnify, and hold
harmless the Company and its owners, affiliates, employees, contractors, and
licensors from third-party claims and reasonable costs arising from your
unlawful use of the Services, material violation of these Terms, lack of
authority to submit Family Content or act for a child, or infringement of
another person's rights.

This obligation does not apply to the extent a claim results from the Company's
own unlawful conduct, negligence, or breach of these Terms.

## 12 Governing law and disputes

Washington law governs these Terms, without regard to conflict-of-law rules.
Before filing a claim, you and the Company agree to make a good-faith effort to
resolve it informally. Contact {publicEnv.siteSupportEmail} with a brief
description of the issue and requested relief.

If informal resolution fails, the state and federal courts with jurisdiction in
King County, Washington will have exclusive jurisdiction, and each party
consents to venue there. You may bring an eligible individual claim in small
claims court. Nothing in this section deprives you of protections or forums that
the law where you live makes mandatory.

## 13 Changes to these Terms

We may update these Terms to reflect changes in the Services, practices, or law.
The date at the top shows when the current version took effect.

If a change materially reduces your rights or changes how child information is
used, we will provide reasonable notice and obtain any new agreement or parental
consent required by law. Updated Terms apply prospectively. If you do not agree,
stop using the Services and cancel before future renewal.

## 14 General terms

These Terms, the Privacy Policy, and offer terms presented at authorization are
the entire agreement concerning the Services and replace earlier agreements
about the same subject. If a provision is unenforceable, it will be modified
only as much as necessary, and the remaining provisions continue in effect.

Our failure to enforce a provision is not a waiver. You may not assign these
Terms or transfer an account without our written consent. We may assign these
Terms as part of a merger, acquisition, reorganization, or sale of assets,
provided mandatory rights remain protected. Headings are for convenience only.

## 15 Contact

The Services are provided by {publicEnv.siteLegalEntity}. Questions, notices,
billing concerns, privacy requests, and account issues may be sent to
{publicEnv.siteSupportEmail}.

<LegalContactDetails />
